import { Lock, Megaphone, MessageCircle, Sparkles, Trophy, Volume2, type LucideIcon } from 'lucide-react';
import type { Community } from '@/types';
import { iconById } from '@/lib/communityIcons';

/** Channel model + display helpers. Channels live in `community_channels`
 *  (see features/communities/hooks/useChannels); the sets below are only the
 *  render-fallback while that query loads, mirroring the server seed exactly.
 *  Messages are scoped by `community_messages.channel_id`. */
export type ChannelKind = 'text' | 'voice';
export interface Channel {
  id: string;
  name: string;
  kind: ChannelKind;
  section: string;
  topic?: string;
  readOnly?: boolean;
  /** Optional custom glyph id (from `@/lib/communityIcons`), set in the Theme Studio. */
  icon?: string;
  /** Optional Discord-style decoration id (from `@/components/ChannelFx`). */
  effect?: string;
}

const WINR_CHANNELS: Channel[] = [
  { id: 'general', name: 'General', kind: 'text', section: 'TEXT CHANNELS' },
  { id: 'voice', name: 'Voice', kind: 'voice', section: 'VOICE CHANNELS' },
];

const DEFAULT_CHANNELS: Channel[] = [
  { id: 'announcements', name: 'announcements', kind: 'text', section: 'INFORMATION', readOnly: true },
  { id: 'rules', name: 'rules', kind: 'text', section: 'INFORMATION', readOnly: true },
  { id: 'general', name: 'general', kind: 'text', section: 'TEXT CHANNELS' },
  { id: 'intros', name: 'introductions', kind: 'text', section: 'TEXT CHANNELS' },
  { id: 'wins', name: 'wins', kind: 'text', section: 'TEXT CHANNELS' },
  { id: 'help', name: 'help', kind: 'text', section: 'TEXT CHANNELS' },
  { id: 'off-topic', name: 'off-topic', kind: 'text', section: 'TEXT CHANNELS' },
  { id: 'lounge', name: 'Lounge', kind: 'voice', section: 'VOICE CHANNELS' },
];

export function getChannels(community: Community): Channel[] {
  return community.id === 'winr-official' ? WINR_CHANNELS : DEFAULT_CHANNELS;
}

const FALLBACK_CHANNEL: Channel = { id: 'general', name: 'general', kind: 'text', section: 'COMMUNITY' };

export function resolveChannel(channels: Channel[], id: string | null): Channel {
  return (
    channels.find((c) => c.id === id) ??
    channels.find((c) => c.id === 'general') ??
    channels[0] ??
    FALLBACK_CHANNEL
  );
}

const GROUP_ORDER = ['INFORMATION', 'TEXT CHANNELS', 'VOICE CHANNELS', 'CHANNELS'];

/** Group channels by section, sorted by the OG groupOrder (unknown sections —
 *  e.g. SUPPORT/COMMUNITY — fall after the known ones, stable by first appearance). */
export function groupChannels(channels: Channel[]): { section: string; items: Channel[] }[] {
  const groups: { section: string; items: Channel[] }[] = [];
  for (const ch of channels) {
    const section = ch.section || 'CHANNELS';
    let g = groups.find((x) => x.section === section);
    if (!g) {
      g = { section, items: [] };
      groups.push(g);
    }
    g.items.push(ch);
  }
  const rank = (s: string) => {
    const i = GROUP_ORDER.indexOf(s);
    return i === -1 ? 99 : i;
  };
  return groups
    .map((g, i) => ({ g, i }))
    .sort((a, b) => rank(a.g.section) - rank(b.g.section) || a.i - b.i)
    .map((x) => x.g);
}

/** Lucide icon for a channel: a custom glyph chosen in the Theme Studio wins,
 *  otherwise fall back to the kind/id-derived OG rail glyph. */
export function channelIcon(ch: Channel): LucideIcon {
  if (ch.icon) return iconById(ch.icon);
  if (ch.kind === 'voice') return Volume2;
  switch (ch.id) {
    case 'wins':
      return Trophy;
    case 'intros':
      return Lock;
    case 'announcements':
      return Megaphone;
    case 'off-topic':
      return Sparkles;
    case 'general':
      return MessageCircle;
    default:
      return MessageCircle;
  }
}

/** >999 → "1.2K" (OG fmt()). */
export function fmtCount(n: number): string {
  return n > 999 ? `${(n / 1000).toFixed(1)}K` : String(n);
}

/** Relative day bucket for message dividers (replaces the OG's raw "8D" strings). */
export function dayLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((startOf(new Date()) - startOf(d)) / 86400000);
  if (diff <= 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return d.toLocaleDateString([], { month: 'long', day: 'numeric' });
}
